import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  getAllQuoteRequests,
  updateQuoteRequestStatus,
} from "@/services/quoteRequestService";
import type { QuoteRequest, QuoteRequestStatus, FirestoreDate } from "@/types/quoteRequest";
import { queryKeys } from "@/lib/queryKeys";
import {
  Search,
  FileText,
  ClipboardList,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

function formatFirestoreDate(value: FirestoreDate | null | undefined): string {
  if (value == null) return "\u2014";
  const d =
    value instanceof Date
      ? value
      : "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function"
        ? (value as { toDate: () => Date }).toDate()
        : "seconds" in value
          ? new Date((value as { seconds: number }).seconds * 1000)
          : null;
  return d
    ? d.toLocaleDateString("es-DO", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "\u2014";
}

const STATUS_LABELS: Record<QuoteRequestStatus, string> = {
  pending: "Pendiente",
  reviewed: "Revisada",
  quoted: "Cotizada",
  rejected: "Rechazada",
};

const STATUS_COLORS: Record<QuoteRequestStatus, string> = {
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200",
  reviewed: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200",
  quoted: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200",
  rejected: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200",
};

type TabFilter = "all" | "pending" | "reviewed" | "quoted" | "rejected";

export function QuoteRequests() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: requests = [], isLoading } = useQuery({
    queryKey: queryKeys.quoteRequests,
    queryFn: getAllQuoteRequests as () => Promise<QuoteRequest[]>,
  });

  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<TabFilter>("all");
  const [selectedRequest, setSelectedRequest] = useState<QuoteRequest | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingRequest, setRejectingRequest] = useState<QuoteRequest | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const filtered = requests.filter((r) => {
    if (activeTab !== "all" && r.status !== activeTab) return false;

    if (search) {
      const term = search.toLowerCase();
      const name = (r.customerName ?? "").toLowerCase();
      const email = (r.customerEmail ?? "").toLowerCase();
      const vehicle = (r.vehicleName ?? "").toLowerCase();
      return name.includes(term) || email.includes(term) || vehicle.includes(term);
    }
    return true;
  });

  // Stats
  const totalCount = requests.length;
  const pendingCount = requests.filter((r) => r.status === "pending").length;
  const quotedCount = requests.filter((r) => r.status === "quoted").length;
  const rejectedCount = requests.filter((r) => r.status === "rejected").length;

  async function handleReview(request: QuoteRequest) {
    setActionLoading(true);
    try {
      await updateQuoteRequestStatus(request.id, "reviewed");
      queryClient.invalidateQueries({ queryKey: queryKeys.quoteRequests });
      toast.success("Solicitud marcada como revisada");
    } catch {
      toast.error("Error al actualizar la solicitud");
    } finally {
      setActionLoading(false);
    }
  }

  function handleCreateQuote(request: QuoteRequest) {
    const params = new URLSearchParams();
    if (request.customerName) params.set("customerName", request.customerName);
    if (request.customerEmail) params.set("customerEmail", request.customerEmail);
    if (request.customerPhone) params.set("customerPhone", request.customerPhone);
    navigate(`/quotes/new?${params.toString()}`);
  }

  function openRejectDialog(request: QuoteRequest) {
    setRejectingRequest(request);
    setRejectDialogOpen(true);
  }

  async function handleReject() {
    if (!rejectingRequest) return;
    setActionLoading(true);
    try {
      await updateQuoteRequestStatus(rejectingRequest.id, "rejected");
      queryClient.invalidateQueries({ queryKey: queryKeys.quoteRequests });
      toast.success("Solicitud rechazada");
      setRejectDialogOpen(false);
      setRejectingRequest(null);
    } catch {
      toast.error("Error al rechazar la solicitud");
    } finally {
      setActionLoading(false);
    }
  }

  function openDetail(request: QuoteRequest) {
    setSelectedRequest(request);
    setAdminNotes(request.adminNotes ?? "");
  }

  async function handleSaveNotes() {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      await updateQuoteRequestStatus(selectedRequest.id, selectedRequest.status, adminNotes);
      queryClient.invalidateQueries({ queryKey: queryKeys.quoteRequests });
      toast.success("Notas guardadas");
      setSelectedRequest(null);
    } catch {
      toast.error("Error al guardar las notas");
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold">Solicitudes de Cotización</h1>
        <p className="text-muted-foreground">
          Solicitudes de cotización enviadas por clientes desde la web
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900/30">
              <ClipboardList className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Solicitudes</p>
              <p className="text-xl font-semibold">{totalCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="rounded-lg bg-yellow-100 p-2 dark:bg-yellow-900/30">
              <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Pendientes</p>
              <p className="text-xl font-semibold">{pendingCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="rounded-lg bg-green-100 p-2 dark:bg-green-900/30">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Cotizadas</p>
              <p className="text-xl font-semibold">{quotedCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="rounded-lg bg-red-100 p-2 dark:bg-red-900/30">
              <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Rechazadas</p>
              <p className="text-xl font-semibold">{rejectedCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabFilter)}>
        <TabsList>
          <TabsTrigger value="all">Todas</TabsTrigger>
          <TabsTrigger value="pending">Pendientes</TabsTrigger>
          <TabsTrigger value="reviewed">Revisadas</TabsTrigger>
          <TabsTrigger value="quoted">Cotizadas</TabsTrigger>
          <TabsTrigger value="rejected">Rechazadas</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="pt-6">
          <div className="mb-4 flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, email o vehículo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-md"
            />
          </div>

          {isLoading ? (
            <p className="text-muted-foreground">Cargando solicitudes...</p>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-muted-foreground">
              <FileText className="h-10 w-10 opacity-50" />
              <p>
                {requests.length === 0
                  ? "No hay solicitudes de cotización."
                  : "Ninguna solicitud coincide con los filtros."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead>Vehículo</TableHead>
                    <TableHead>Mensaje</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatFirestoreDate(r.createdAt)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {r.customerName || "\u2014"}
                      </TableCell>
                      <TableCell>
                        <span className="block">{r.customerPhone || "\u2014"}</span>
                        {r.customerEmail && (
                          <span className="block text-xs text-muted-foreground">
                            {r.customerEmail}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {r.vehicleName || "\u2014"}
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <span className="line-clamp-2 text-sm text-muted-foreground">
                          {r.message || "\u2014"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[r.status] ?? ""}`}
                        >
                          {STATUS_LABELS[r.status] ?? r.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetail(r)}
                            title="Ver detalle"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {r.status === "pending" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700"
                              onClick={() => handleReview(r)}
                              disabled={actionLoading}
                              title="Marcar como revisada"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                          {(r.status === "pending" || r.status === "reviewed") && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => handleCreateQuote(r)}
                              title="Crear cotización"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          )}
                          {r.status !== "rejected" && r.status !== "quoted" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => openRejectDialog(r)}
                              disabled={actionLoading}
                              title="Rechazar"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={(open) => !open && setSelectedRequest(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalle de Solicitud</DialogTitle>
            <DialogDescription>
              Información completa de la solicitud de cotización
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Cliente</p>
                  <p className="font-medium">{selectedRequest.customerName || "\u2014"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Estado</p>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[selectedRequest.status] ?? ""}`}
                  >
                    {STATUS_LABELS[selectedRequest.status] ?? selectedRequest.status}
                  </span>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p>{selectedRequest.customerEmail || "\u2014"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Teléfono</p>
                  <p>{selectedRequest.customerPhone || "\u2014"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Vehículo</p>
                  <p>{selectedRequest.vehicleName || "\u2014"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Fecha</p>
                  <p>{formatFirestoreDate(selectedRequest.createdAt)}</p>
                </div>
              </div>

              {selectedRequest.message && (
                <div>
                  <p className="mb-1 text-sm text-muted-foreground">Mensaje del cliente</p>
                  <p className="whitespace-pre-wrap rounded-md border bg-muted/50 p-3 text-sm">
                    {selectedRequest.message}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Notas del administrador</p>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Agregar notas internas..."
                  rows={3}
                />
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setSelectedRequest(null)}
                >
                  Cerrar
                </Button>
                <Button onClick={handleSaveNotes} disabled={actionLoading}>
                  {actionLoading ? "Guardando..." : "Guardar Notas"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar Solicitud</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas rechazar la solicitud de{" "}
              &quot;{rejectingRequest?.customerName}&quot;? Esta acción cambiará el estado a rechazado.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={actionLoading}>
              {actionLoading ? "Rechazando..." : "Rechazar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
